import { Module } from 'vuex';

import { Feedback } from '@/modules/feedbacks/domain/feedback';
import { FeedbackAverage } from '@/modules/feedbacks/domain/feedback-average';
import { FeedbackCard } from '@/modules/feedbacks/domain/feedback-card';
import { FeedbackComment } from '@/modules/feedbacks/domain/feedback-comment';
import { FeedbackCount } from '@/modules/feedbacks/domain/feedback-count';
import { FeedbackType, getFeedbackType } from '@/modules/feedbacks/domain/feedback-types';
import { FeedbacksResults } from '@/modules/feedbacks/domain/feedbacks-results';
import { UserFeedback } from '@/modules/feedbacks/domain/user-feedback';
import feedbacksRepository from '@/modules/feedbacks/repositories/feedbacks.repository';
import { FeedbacksActions } from '@/modules/feedbacks/store/feedbacks.actions';
import { FeedbacksGetters } from '@/modules/feedbacks/store/feedbacks.getters';
import { FeedbacksMutations } from '@/modules/feedbacks/store/feedbacks.mutations';
import { FeedbacksState } from '@/modules/feedbacks/store/feedbacks.states';

const feedbacks: Module<FeedbacksState, any> = {
    namespaced: true,
    state: {
        allFeedbacks: {
            limit: '0',
            start: '0',
            pages: 1,
            total: 0,
            results: [],
        },
        feedbackAverage: [],
        feedbackTypesCount: [],
        feedbackCountByAppreciation: [],
        suggestionsOfTheMonth: {
            limit: '0',
            start: '0',
            pages: 1,
            total: 0,
            results: [],
        },
    },
    getters: {
        [FeedbacksGetters.GET_ALL_FEEDBACKS](state): FeedbacksResults {
            return state.allFeedbacks;
        },
        [FeedbacksGetters.GET_FEEDBACK_AVERAGE](state): number {
            return state.feedbackAverage.length ? Number(state.feedbackAverage[0].average) : 0;
        },
        [FeedbacksGetters.GET_FEEDBACK_CARDS](state): FeedbackCard[] {
            const result: FeedbackCard[] = [];

            for (let appreciation = 1; appreciation <= 5; appreciation++) {
                const feedback = state.feedbackCountByAppreciation.find(
                    feedback => feedback.appreciation === appreciation,
                );
                let card: FeedbackCard;

                if (feedback) {
                    card = {
                        categoryId: Number(feedback.appreciation),
                        category: String(feedback.appreciation),
                        total: feedback.total,
                    };
                } else {
                    card = {
                        categoryId: appreciation,
                        category: String(appreciation),
                        total: 0,
                    };
                }

                card.icon = 'mdi-star';
                card.cssClass = `summary-feedbacks-${appreciation}`;

                result.push(card);
            }

            return result;
        },
        [FeedbacksGetters.GET_ALL_COMMENTS](state: FeedbacksState): FeedbackComment[] {
            return state.allFeedbacks.results.map((result: Feedback) => {
                return {
                    type: getFeedbackType((result.feedbackType as FeedbackType).name),
                    rating: result.appreciation,
                    opinion: result.content,
                    client: (result.user as UserFeedback).username,
                    contact: `${(result.user as UserFeedback).name} ${
                        (result.user as UserFeedback).lastName
                    }`,
                    nameCustomer: (result.user as UserFeedback).customer.name,
                    date: result.date!,
                };
            });
        },
        [FeedbacksGetters.GET_MONTH_SUGGESTIONS](state: FeedbacksState): FeedbacksResults {
            return state.suggestionsOfTheMonth;
        },
        [FeedbacksGetters.GET_SUGGESTIONS](state: FeedbacksState): FeedbackComment[] {
            return state.suggestionsOfTheMonth.results.map((result: Feedback) => {
                return {
                    type: getFeedbackType((result.feedbackType as FeedbackType).name),
                    opinion: result.content,
                    client: (result.user as UserFeedback).username,
                    contact: `${(result.user as UserFeedback).name} ${
                        (result.user as UserFeedback).lastName
                    }`,
                    nameCustomer: (result.user as UserFeedback).customer.name,
                    date: result.date!,
                };
            });
        },
    },
    mutations: {
        [FeedbacksMutations.SET_ALL_FEEDBACKS](
            state: FeedbacksState,
            allFeedbacks: FeedbacksResults,
        ) {
            state.allFeedbacks = allFeedbacks;
        },
        [FeedbacksMutations.SET_FEEDBACK_AVERAGE](
            state: FeedbacksState,
            feedbackAverage: FeedbackAverage[],
        ) {
            state.feedbackAverage = feedbackAverage;
        },
        [FeedbacksMutations.SET_FEEDBACK_TYPE_COUNT](
            state: FeedbacksState,
            feedbackCount: FeedbackCount[],
        ) {
            state.feedbackCountByAppreciation = feedbackCount;
        },
        [FeedbacksMutations.SET_MONTH_SUGGESTIONS](
            state: FeedbacksState,
            suggestions: FeedbacksResults,
        ) {
            state.suggestionsOfTheMonth = suggestions;
        },
    },
    actions: {
        async [FeedbacksActions.FETCH_FEEDBACKS](
            { commit },
            options: { limit: number; start: number; type?: number; rating?: number },
        ): Promise<RequestResponse> {
            try {
                const response = await feedbacksRepository.getFeedbackDashboard(
                    options.limit,
                    options.start,
                    options.type,
                    options.rating,
                );
                commit(FeedbacksMutations.SET_ALL_FEEDBACKS, response.allFeedbacks);
                commit(FeedbacksMutations.SET_FEEDBACK_AVERAGE, response.feedbackAverage);
                commit(
                    FeedbacksMutations.SET_FEEDBACK_TYPE_COUNT,
                    response.feedbackCountByAppreciation,
                );

                return false;
            } catch (e) {
                // @ts-ignore: e is unknown, but is an error!
                return new Exception(e.response);
            }
        },
        async [FeedbacksActions.FETCH_SUGGESTIONS](
            { commit },
            options: { limit: number; start: number },
        ): Promise<RequestResponse> {
            try {
                const response = await feedbacksRepository.getMonthSuggestions(
                    options.limit,
                    options.start,
                );
                commit(FeedbacksMutations.SET_MONTH_SUGGESTIONS, response);

                return false;
            } catch (e) {
                // @ts-ignore: e is unknown, but is an error!
                return new Exception(e.response);
            }
        },
        async [FeedbacksActions.CREATE_FEEDBACK](
            { commit },
            feedback: Feedback,
        ): Promise<RequestResponse> {
            try {
                await feedbacksRepository.createFeedback(feedback);
                return false;
            } catch (e) {
                // @ts-ignore: e is unknown, but is an error!
                return new Exception(e.response);
            }
        },
    },
};

export default feedbacks;
